import type { ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/cn";
import type { BadgeTono } from "./Badge";
import { Card } from "./Card";

export interface MetricCardProps {
  titulo: string;
  /** Valor ya formateado (usar lib/format). */
  valor: string;
  /** Se dibuja justo debajo del valor (ej. una `BarraProgreso`), antes del detalle. */
  debajoValor?: ReactNode;
  detalle?: ReactNode;
  /** Color del valor (ej. saldo a favor en verde, deuda en rojo). Sin tono, el color de texto normal. */
  tono?: BadgeTono;
  /** Ruta: toda la tarjeta se toca y lleva ahí (el link es el título, estirado sobre la tarjeta). */
  to?: string;
  className?: string;
}

const COLOR_VALOR: Record<BadgeTono, string> = {
  neutro: "text-texto",
  primario: "text-primario",
  exito: "text-exito",
  alerta: "text-alerta",
  peligro: "text-peligro",
};

// El título va como los rótulos de la presentación: chico, en mayúsculas espaciadas.
const CLASE_TITULO = "text-[0.6875rem] font-medium uppercase tracking-[0.2em] text-texto-suave";

/** Tarjeta de una cifra clave (ej. "Avance de obra 42,5 %"). */
export function MetricCard({ titulo, valor, debajoValor, detalle, tono, to, className }: MetricCardProps) {
  return (
    <Card className={cn("flex flex-col gap-1", to && "relative transition-colors hover:border-primario/40 focus-within:ring-2 focus-within:ring-primario/40", className)}>
      {to ? (
        <span className={cn("flex items-center justify-between gap-2", CLASE_TITULO)}>
          <Link to={to} className="after:absolute after:inset-0 after:rounded-tarjeta focus:outline-none">
            {titulo}
          </Link>
          <span aria-hidden="true" className="text-primario">›</span>
        </span>
      ) : (
        <span className={CLASE_TITULO}>{titulo}</span>
      )}
      <span
        className={cn(
          "mt-2 text-cifra font-extralight tabular-nums tracking-[-0.04em]",
          tono && COLOR_VALOR[tono],
        )}
      >
        {valor}
      </span>
      {debajoValor}
      {detalle && <span className="text-chico text-texto-suave">{detalle}</span>}
    </Card>
  );
}
